import { AGGClient } from 'src/clients/agg-client/agg-client';

const createQuery = (accountId: string) => {
	return `#graphql
    query InboundSalesQuery {
        growthUnifiedProfile_getUnifiedProfile(accountId: "${accountId}"){
            userProfile{
                domainType
                smbUserVisitedPricingPages
            }
            sites{
                cloudId
                displayName
                products{
                    productKey
                    productName
                    productEdition
                }
            }
        }
    }
`;
};

/**
 * Builds the headers every GUPS sales-inbound lookup needs.
 *
 * X-Slauth-User-Context-Account-Id is only for local testing. In a deployed
 * environment, the service proxy translates the user context token into this
 * header.
 */
const buildSalesInboundHeaders = (accountId: string): { [key: string]: string } => {
	const customHeaders: { [key: string]: string } = {
		'x-use-case': 'sales-inbound',
	};

	if (process.env['NODE_ENV'] === 'development') {
		customHeaders['X-Slauth-User-Context-Account-Id'] = accountId;
	}

	return customHeaders;
};

interface GetIsSMBSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

type InboundSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getUnifiedProfile: {
			sites: Array<{
				cloudId: string;
				displayName: string;
				products: Array<{
					productEdition: string;
					productKey: string;
					productName: string;
				}>;
			}>;
			userProfile: {
				domainType: string;
				smbUserVisitedPricingPages: boolean;
			};
		};
	};
};

export const getIsSMBSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsSMBSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundSalesQueryResponse>(
		createQuery(accountId),
		undefined,
		buildSalesInboundHeaders(accountId),
	);

	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const isSMBSalesLead =
		data?.growthUnifiedProfile_getUnifiedProfile?.userProfile?.smbUserVisitedPricingPages;

	return isSMBSalesLead;
};

/**
 * `accountId` is passed as a GraphQL variable rather than interpolated into the
 * document, so it goes through the variables mechanism instead of string
 * concatenation. (`createQuery` above still interpolates; that is the shipped
 * SMB path and is deliberately left alone here.)
 *
 * The variable is typed `ID!`, not `String!`. `growthUnifiedProfile_getAccountProfile`
 * is absent from `graphql-types.generated.ts`, so the argument type cannot be
 * confirmed from the generated schema — but the sibling
 * `QueryGrowthUnifiedProfile_GetUnifiedProfileArgs` types the same `accountId`
 * argument as `InputMaybe<Scalars['ID']>`. A non-null `ID!` is a valid usage
 * wherever either `ID` or `ID!` is expected, so it holds in both cases, whereas
 * `String!` would fail validation against an `ID` argument. That failure mode is
 * silent: validation errors surface as `errors`, which this helper throws on, the
 * caller swallows, and the CTA then never renders for anyone.
 */
const ACCOUNT_PROFILE_QUERY = `#graphql
    query InboundEnterpriseSalesQuery($accountId: ID!) {
        growthUnifiedProfile_getAccountProfile(accountId: $accountId){
            companyProfile{
                accountSegment
            }
        }
    }
`;

/**
 * Account segments that receive the Enterprise "Contact us" CTA.
 *
 * GUPS returns exactly one of: Strategic | SMB Plus | Mid-Market | SMB |
 * Government | Enterprise. Confirmed with the GUPS team on 2026-09-18; these
 * literals match the warehouse `dim_company.account_segment` vocabulary, so no
 * mapping layer is required.
 *
 * Do NOT confuse this with `enterprise_account_status`
 * (Platinum / GAM / Gold / Bronze / ...), which is Enterprise *tiering* rather
 * than segment and is a different field entirely.
 */
const ENTERPRISE_ACCOUNT_SEGMENTS = ['Enterprise', 'Strategic'];

interface GetIsEnterpriseSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

/**
 * Hand-written because the generated schema does not yet know these fields:
 * `GrowthUnifiedProfileCompanyProfile` in graphql-types.generated.ts declares
 * only `companyType`. This mirrors the precedent set by
 * `InboundSalesQueryResponse` above — the query is not type-checked against the
 * schema until a regeneration lands.
 */
type InboundEnterpriseSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getAccountProfile: {
			companyProfile: {
				accountSegment: string | null;
			};
		};
	};
};

export const getIsEnterpriseSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsEnterpriseSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundEnterpriseSalesQueryResponse>(
		ACCOUNT_PROFILE_QUERY,
		{ accountId },
		buildSalesInboundHeaders(accountId),
	);

	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const accountSegment =
		data?.growthUnifiedProfile_getAccountProfile?.companyProfile?.accountSegment;

	// A missing or unrecognised segment is not eligible. Deliberately no
	// fallback to annualRevenue or any other signal — inferring the segment
	// would silently redefine the experiment audience mid-flight.
	if (!accountSegment) {
		return false;
	}

	return ENTERPRISE_ACCOUNT_SEGMENTS.includes(accountSegment);
};
